import { Button, Result, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundaryInner extends Component<Props & { t: TFunction }, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--ant-color-bg-container)',
          }}
        >
          <Result
            status="error"
            title={t('common.error_boundary_title', 'Something went wrong')}
            subTitle={t(
              'common.error_boundary_subtitle',
              'The application encountered an unexpected error. Please try reloading the page.',
            )}
            extra={[
              <Button type="primary" key="reload" onClick={this.handleReload}>
                {t('common.reload', 'Reload Page')}
              </Button>,
            ]}
          >
            <div className="desc">
              <Paragraph>
                <Text
                  strong
                  style={{
                    fontSize: 16,
                  }}
                >
                  {t('common.error_detail', 'Error Details:')}
                </Text>
              </Paragraph>
              <Paragraph>
                <Text type="danger">{this.state.error?.toString()}</Text>
              </Paragraph>
              {this.state.errorInfo && (
                <pre
                  style={{
                    maxWidth: '800px',
                    overflow: 'auto',
                    padding: '12px',
                    background: 'var(--ant-color-fill-tertiary)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper to use translation hook
export default function ErrorBoundary({ children }: Props) {
  const { t } = useTranslation();
  return <ErrorBoundaryInner t={t}>{children}</ErrorBoundaryInner>;
}
